import React from 'react';

import {detectDeezerWebUser} from '@modules/auth/deezerWebUser.client';
import type {AuthSessionResponse} from '@modules/auth/types';

export type AuthStatus = 'IDLE' | 'LOADING' | 'READY' | 'ERROR';

export type AuthState = {
	status: AuthStatus;
	case: AuthSessionResponse['case'] | null;
	userId: string | null;
	errorCode: string | null;
};

export type AuthContextValue = [
	AuthState,
	{
		ensureSession: () => Promise<AuthSessionResponse>;
		loadIdentity: () => Promise<string | null>;
		reset: () => void;
	},
];

const initialState: AuthState = {
	status: 'IDLE',
	case: null,
	userId: null,
	errorCode: null,
};

const AuthContext = React.createContext<AuthContextValue | null>(null);

export const AuthProvider = ({children}: {children?: React.ReactNode}) => {
	const [state, setState] = React.useState<AuthState>(initialState);
	// Deduplicate concurrent ensureSession() calls (pattern borrowed from
	// standalone-mydeezerstory's getTokenPromiseRef). Prevents 2 POSTs to
	// /api/auth/session from racing and creating 2 Deezer accounts when
	// no cookie is set yet (e.g. CTA hero + /import polling fire at once).
	const inflightRef = React.useRef<Promise<AuthSessionResponse> | null>(null);
	const identityInflightRef = React.useRef<Promise<string | null> | null>(null);

	const ensureSession =
		React.useCallback(async (): Promise<AuthSessionResponse> => {
			if (inflightRef.current) return inflightRef.current;

			setState((s) => ({...s, status: 'LOADING', errorCode: null}));
			const promise = (async () => {
				try {
					// Detect the Deezer user from the browser before signing
					// in: gw-light is IP-bound, so the server-side check
					// fails on preview pods. Forward only a server-verified
					// claim; the API response exposes only the user id needed
					// for Segment attribution, never auth/TMM tokens.
					const detection = await detectDeezerWebUser();
					const claim: Record<string, string | null> = {};
					if (detection.status === 'logged_in') {
						claim.deezerWebUserId = detection.user.userId;
					} else if (detection.status === 'anonymous') {
						claim.deezerWebUserId = null;
					}
					const res = await fetch('/api/auth/session', {
						method: 'POST',
						credentials: 'include',
						headers: {'Content-Type': 'application/json'},
						body: JSON.stringify(claim),
					});
					const json = (await res.json()) as
						| AuthSessionResponse
						| {code: string};

					if (!res.ok || !('case' in json)) {
						throw new Error('code' in json ? json.code : 'UNKNOWN');
					}

					setState({
						status: 'READY',
						case: json.case,
						userId: json.userId ?? null,
						errorCode: null,
					});
					return json;
				} catch (err) {
					const code = err instanceof Error ? err.message : 'NETWORK';
					setState((s) => ({...s, status: 'ERROR', errorCode: code}));
					throw err;
				} finally {
					inflightRef.current = null;
				}
			})();

			inflightRef.current = promise;
			return promise;
		}, []);

	const loadIdentity = React.useCallback(async (): Promise<string | null> => {
		if (identityInflightRef.current) return identityInflightRef.current;

		const promise = (async () => {
			const res = await fetch('/api/auth/identity', {
				method: 'GET',
				credentials: 'include',
			});
			const json = (await res.json()) as {userId?: unknown} | {code: string};
			if (!res.ok) {
				throw new Error('code' in json ? json.code : 'IDENTITY_FAILED');
			}

			const userId =
				'userId' in json && typeof json.userId === 'string'
					? json.userId
					: null;
			setState((s) => ({
				...s,
				userId,
			}));
			return userId;
		})();

		identityInflightRef.current = promise;
		try {
			return await promise;
		} finally {
			identityInflightRef.current = null;
		}
	}, []);

	const reset = React.useCallback(() => setState(initialState), []);

	const value = React.useMemo<AuthContextValue>(
		() => [state, {ensureSession, loadIdentity, reset}],
		[state, ensureSession, loadIdentity, reset],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
	const ctx = React.useContext(AuthContext);
	if (!ctx) {
		throw new Error('useAuth must be used within <AuthProvider>');
	}
	return ctx;
};
